import type { Draft } from 'immer';
import { createStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { StoreApi } from 'zustand/vanilla';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { useConfigurationStore } from '@/stores/configuration';
import { addTranslateToast } from './toast';

export type MultiselectActionType = 'delete' | 'forward' | 'default';

interface ConversationState {
  type: ConversationType;
  id: number;
  layout?: 'full' | 'mini';
  isAdminSession?: boolean;
  isMediaPlaying: string | null;
  multiselectAction: MultiselectActionType | null;
  selectedMessages: ConversationMessageTypeUnion[];
}

interface ConversationActions {
  setMediaPlaying(isPlaying: string): void;
  clearMediaPlaying(): void;

  setMultiselectAction(action: MultiselectActionType): void;

  addSelectedMessages(messages: ConversationMessageTypeUnion[]): void;
  removeSelectedMessages(messages: Array<ConversationMessageTypeUnion | number>): void;
  resetSelectedMessages(messages?: ConversationMessageTypeUnion[]): void;
  toggleSelectedMessage(message: ConversationMessageTypeUnion): void;

  resetMultiselect(): void;
}

export type ConversationStore = ConversationState & ConversationActions;

export type ConversationStoreType = Readonly<StoreApi<ConversationStore>>;

export const createConversationStore = (
  type: ConversationType,
  id: number,
  isAdminSession?: boolean,
  layout?: 'full' | 'mini',
) =>
  createStore<ConversationStore>()(
    immer(
      (set): ConversationStore => ({
        type,
        id,
        layout,
        isAdminSession,
        isMediaPlaying: null,
        multiselectAction: null,
        selectedMessages: [],

        setMediaPlaying(isPlaying) {
          set({ isMediaPlaying: isPlaying });
        },
        clearMediaPlaying() {
          set({ isMediaPlaying: null });
        },

        setMultiselectAction(action) {
          set({ multiselectAction: action });
        },

        addSelectedMessages(messages) {
          set((state) => {
            tryAddMessagesToState(state, messages);
          });
        },
        removeSelectedMessages(messages) {
          set((state) => {
            const messageIds = messages.map((m) => (typeof m === 'number' ? m : m.ID));

            state.selectedMessages = state.selectedMessages.filter((m) => messageIds.includes(m.ID));
          });
        },
        toggleSelectedMessage(message) {
          set((state) => {
            const index = state.selectedMessages.findIndex((m) => m.ID === message.ID);

            if (index === -1) {
              tryAddMessagesToState(state, [message]);
            } else {
              state.selectedMessages.splice(index, 1);
            }
          });
        },
        resetSelectedMessages(messages = []) {
          set({ selectedMessages: messages });
        },

        resetMultiselect() {
          set({ multiselectAction: null, selectedMessages: [] });
        },
      }),
    ),
  );

function tryAddMessagesToState(state: Draft<ConversationStore>, messages: ConversationMessageTypeUnion[]) {
  const maxForwardCount = useConfigurationStore.getState().envConfig.config?.maxForwardMessageCount ?? 100;

  if (state.selectedMessages.length + messages.length > maxForwardCount) {
    addTranslateToast({
      type: 'warning',
      message: `errors.messages.length|max=${maxForwardCount}`,
    });
  } else {
    state.selectedMessages.push(...messages);
  }
}
